import { Icon } from "@iconify/react";
import React, { useCallback, useEffect, useState } from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
  );
}

function installHelpCopy(): string {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return "Tap Share (□↑) → Add to Home Screen → Add. If the option is missing, open this site in Safari first.";
  }
  if (/Android/i.test(ua)) {
    return "Open the browser menu (⋮), then tap Install app or Add to Home screen. If you don’t see it, try Chrome’s menu → Install app, or clear site data and reload once.";
  }
  return "Use the install (⊕) icon in the address bar if shown, or the browser menu → Install Dondaa Dashboard. After uninstalling, the install prompt may not appear again until you reload or clear site data for this site.";
}

/**
 * Install control: uses native prompt when `beforeinstallprompt` fires; otherwise shows manual steps.
 * The browser often does not fire `beforeinstallprompt` again right after uninstall — the fallback covers that.
 */
const PwaInstallButton = () => {
  const [standalone, setStandalone] = useState(() => isStandalone());
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(
    null
  );
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (standalone) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstallEvent(null);
      setStandalone(isStandalone());
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [standalone]);

  const onInstallClick = useCallback(async () => {
    if (installEvent) {
      await installEvent.prompt();
      await installEvent.userChoice;
      setInstallEvent(null);
      return;
    }
    setShowHelp(true);
  }, [installEvent]);

  if (standalone) {
    return null;
  }

  return (
    <li className="notification-list">
      <button
        type="button"
        className="nav-link arrow-none border-0 bg-transparent text-left"
        style={{ cursor: "pointer" }}
        onClick={onInstallClick}
        title={
          installEvent
            ? "Install Dondaa Dashboard"
            : "Install Dondaa Dashboard — tap for steps if your browser doesn’t offer install"
        }
        aria-label="Install Dondaa Dashboard"
      >
        <i className="dripicons-download noti-icon">
          <Icon icon="mdi:download-outline" />
        </i>
        <span className="d-none d-xl-inline ml-1">Install Dondaa Dashboard</span>
      </button>

      {showHelp ? (
        <div
          className="position-fixed d-flex align-items-center justify-content-center p-3"
          style={{
            inset: 0,
            zIndex: 9999,
            backgroundColor: "rgba(0,0,0,0.45)",
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pwa-install-help-title"
          onClick={() => setShowHelp(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setShowHelp(false);
          }}
        >
          <div
            className="bg-white rounded shadow p-4"
            style={{ maxWidth: 420, width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 id="pwa-install-help-title" className="mb-3">
              Install Dondaa Dashboard
            </h5>
            <p className="text-muted mb-3" style={{ fontSize: "0.95rem" }}>
              {installHelpCopy()}
            </p>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowHelp(false)}
            >
              OK
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
};

export default PwaInstallButton;
